import * as THREE from "three";
import Constants from '../../Constants.js'
import {removeElementFromArray, isRug, getObjectFromRootByName, detachSceneAssets, attachSceneAssets} from "../../HelperFunctions"
import LostAndFoundArea from '../../../LostAndFoundArea.js'
import LinearGrid from './Grid/LinearGrid.js'
import MatrixGrid from './Grid/MatrixGrid.js'
import WallGrid from './WallGrid/WallGrid.js'
import { PVB } from '../../../PVB.js'
import {ConvexGeometry} from '../../../ConvexGeometry.js'


export default class AutoPlacementManager {

    collection = null;
    sceneAssets = null;
    itemCategoryList = null;
    relationshipsList = null;
    debugEngine = null;
    assetManager = null;
    raycastManager = null;
    space = null;
    scene = null;
    managersDict = null;
    lostAndFoundAreaItems = null;
    sceneToLoadAssets = null; 
    orderedAssetList = {};
    orphans = {};
    unplacedRugs = [];
    unplacedFloor = [];
    unplacedCeiling = [];
    unplacedWall = [];
    unplacedItems = [];
    shouldInvertNormals = true;
    floorGrids = {};
    ceilingGrids = {};
    wallGrids = {};
    floorRugGrids = {};
    preplacedAssets = null;
    runHardCodedPlacement = false;
    isBabylonExported = false;

    /**
     * Auto placement manager auto places new assets in the scene and saved assets in their fixed positions.
     * @param {THREE.Scene} scene - The main Three JS scene that contains all of the objects.
     * @param {Array} sceneAssets - An array that contains references to all the assets currently in the scene.
     * @param {Array} sceneToLoadAssets - The array containing stored assets which are to be loaded in the scene.
     * @param {Object} itemCategoryList - A dictionary that contains all the categories along with their ids. 
     * @param {Object} relationshipsList - A dictionary representing parent child relationship between categories.
     * @param {Array} lostAndFoundAreaItems - An array that contains the assets to be placed in lost and found area.
     * @param {Object} managersDict - A dictionary that contains all the available managers.
     */
    constructor( scene, sceneAssets, sceneToLoadAssets, itemCategoryList, itemsPlacementList, itemMaterialTypes, relationshipsList, lostAndFoundAreaItems, managersDict) {
        this.managersDict = managersDict;
        this.sceneAssets = sceneAssets;
        this.itemCategoryList = itemCategoryList;
        this.relationshipsList = relationshipsList;
        this.itemsPlacementList = itemsPlacementList;
        this.itemMaterialTypes = itemMaterialTypes;
        this.lostAndFoundAreaItems = lostAndFoundAreaItems;
        this.scene = scene;
        this.sceneToLoadAssets = sceneToLoadAssets;
        this.floorGrids = {};
        this.setUpPreAutoPlacementManagers();
    }

    /**
     * Load required managers needed during auto placement
     */
    setUpPreAutoPlacementManagers() {
        this.debugEngine = this.managersDict[Constants.Manager.DebugEngine];
        this.space = this.managersDict[Constants.Manager.SpaceManager];
        this.assetManager = this.managersDict[Constants.Manager.AssetLoadingManager]
        this.raycastManager = this.managersDict[Constants.Manager.RaycastManager];
    }

    /**
     * Setup Lost and Found area needed during auto placement
     */
    setupLostAndFoundArea() {
        if(this.lostAndFoundAreaItems != null && this.lostAndFoundAreaItems.length > 0){
            this.debugEngine.debugLog("LFA items:, ", this.lostAndFoundAreaItems);
            this.LFAAreaManager = new LostAndFoundArea(this.lostAndFoundAreaItems);

            let box = new THREE.Box3();
            box.setFromObject(this.space.scene);
            let sceneSpaceSize = new THREE.Vector3();
            box.getSize(sceneSpaceSize);
            
            let LFAAreaRoot = this.LFAAreaManager.areaObj.root;
            LFAAreaRoot.position.y += this.LFAAreaManager.size.y / 2;
            LFAAreaRoot.position.x -= (sceneSpaceSize.x/2 + this.LFAAreaManager.size.x/2 + 0.5)
            this.scene.add(LFAAreaRoot);

            this.LFAAreaManager.box.setFromObject(this.LFAAreaManager.areaObj.root);
        
            this.shouldInvertNormals = false;
            this.space.floors.push(this.LFAAreaManager.areaObj.floor);
            this.placeLoadedAssets(LFAAreaRoot, this.LFAAreaManager.areaObj, this.lostAndFoundAreaItems, true);

            this.space.scene.add(LFAAreaRoot);
            
            this.space.ceilings.push(this.LFAAreaManager.areaObj.ceiling);
            this.LFAAreaManager.areaObj.walls.forEach(element => {
                this.space.walls.push(element);
            });

            this.managersDict[Constants.Manager.LostAndFoundAreaManager] = this.LFAAreaManager;
            this.debugEngine.debugLog("lost and found", this.lostAndFoundAreaItems)
        }
    }

    /**
     * Set up miscellaneous parts of the space so they can be detected during raycasting too
     */
    setMiscAssetsInScene() {
        let parentSceneForMisc = new THREE.Scene();
        for (let i in this.space.miscNodes) {
            this.space.miscNodes[i].userData.rootNode = parentSceneForMisc.id;
            this.space.miscNodes[i].userData.isSceneMisc = true;
        }
        this.scene.add(parentSceneForMisc);
    }

    /**
     * Setup asset and its properties to be added to the scene
     * @param {String} objectName - The name of the asset to be set
     * @param {Boolean} objectVisible - The state of the asset in the scene creator deleted or not deleted
     */
    setupObject(objectName, objectVisible = true, assetPlacement = null) {
        let assetObj = this.assetManager.getAsset(objectName);
        if( assetObj != undefined ) {
            assetObj = assetObj.clone();
            let assetPlacementType = assetPlacement ?? this.getPlacementType( assetObj );
            
            this.setupObjectUserData(assetObj, assetPlacementType, Constants.AssetType.ITEM, objectVisible);

            assetObj.traverse( ( child ) => {
                if( child.isMesh ) {
                    let newMat          = child.material.clone();
                    child.material      = newMat;
                    child.material.side = THREE.DoubleSide;
                    child.material.envMap   = this.envMap;
                    child.castShadow        = true;
                    child.receiveShadow     = true;
                    child.userData.rootNode = assetObj.id;
                    child.userData.isSceneAsset = true;
                    child.visible = objectVisible;
                }


            } );
            assetObj.userData.visible = objectVisible;
            this.setupObjectConvexHull(assetObj);
        }

        return assetObj;
    }

    /** 
     * Setup convex hull for an asset
     * @param {THREE.Scene} assetObj - The asset object whose convex hull is to be set
     */
    setupObjectConvexHull(assetObj) {
        let convexGeometry = new THREE.Object3D();
        let meshMaterial = new THREE.MeshBasicMaterial( {
            color: 0xffffff
        } );

        let meshGeometry = new ConvexGeometry( assetObj );

        // Setup mesh & its properties
        let mesh = new THREE.Mesh( meshGeometry, meshMaterial );
        mesh.material.side = THREE.DoubleSide;
        mesh.renderOrder = 0;
        mesh.userData.rootNode = assetObj.id;
        convexGeometry.add( mesh );

        // Make invisible
        meshMaterial.transparent = true;
        meshMaterial.opacity = 0;
        meshMaterial.depthWrite = false;
        
        assetObj.add(convexGeometry);
        assetObj.userData.convexGeometry = convexGeometry;
    }

    /**
     * Setup the object user data properties 
     * @param {THREE.Scene} object - The asset object whose user data properties are to be set
     * @param {String} placementType - The placement type of the user data
     * @param {String} assetType - The type of object in Scene Creator - item in case of asset
     * @param {*} assetVisible  - The state of the object in Scene Creator - deleted or not deleted
     */
    setupObjectUserData(object, placementType, assetType, assetVisible) {
        object.userData.placementType 	= placementType;
        object.userData.assetType 		= assetType;
        object.userData.size            = ( new THREE.Box3().setFromObject( object ).getSize( new THREE.Vector3() ) );
        object.userData.scale           = new THREE.Vector3(1,1,1);
        object.userData.pvb             = new PVB( object );
        object.userData.visible         = assetVisible;

        if ( this.isPillow( object ) ) { object.userData.isPillow = true; }

        object.userData.isRug = this.getMaterialType( object ) == "Rug";
    }

    /**
     * Return if the asset is a pillow or not
     * @param {THREE.Scene} item  - The asset object to pass through the pillow check
     */
    isPillow ( item ) {
        return ( this.itemCategoryList[ item.name ] != undefined &&  this.itemCategoryList[ item.name ].toLowerCase().includes("pillow") );
    }

    /**
     * Return if the asset is a blanket or a throw
     * @param {THREE.Scene} item - The asset object to pass through the blanket / throw check
     */
    isBlanketOrThrow ( item ) {
        return ( this.itemCategoryList[ item.name ] != undefined &&
            ( this.itemCategoryList[ item.name ] == "blankets" ||
            this.itemCategoryList[ item.name ] == "Blankets & Throws" ) );
    }

    /**
    * Update placement type list
    * @param {Object} itemsPlacementList - The placement types of objects to be auto placed
    */
    updateItemsPlacementList(itemsPlacementList){
        this.itemsPlacementList = Object.assign(itemsPlacementList, this.itemsPlacementList)
    }


    updateItemsPlacementofObject(objectName, placementType) {
        this.itemsPlacementList[objectName] = placementType;
    }

    /**
    * Update category list
    * @param {Object} itemCategoryList - The placement types of objects to be auto placed
    */
    updateItemsCategoryList(itemCategoryList) {
        this.itemCategoryList = Object.assign(itemCategoryList, this.itemCategoryList)
    }

    /**
    * Update material types list
    * @param {Object} itemMaterialTypesList - The placement types of objects to be auto placed
    */
    updateItemsMaterialTypesList(itemMaterialTypesList) {
        this.itemMaterialTypesList = Object.assign(itemMaterialTypesList, this.itemMaterialTypesList)
    }

    /**
     * Return the placement type of the asset floor/wall/ceiling
     * @param {THREE.Scene} object - The asset object whose placement type is required
     */
    getPlacementType( object ) {

        let placementType = this.itemsPlacementList[object.name];
        
        if (object.userData.currentPlacement) {
            return object.userData.currentPlacement
        }

        if ( placementType != undefined && placementType != null && placementType != "") {
            return placementType.toLowerCase();
        }

        if ( this.isBlanketOrThrow( object ) ) {
            return Constants.PlacementType.FLOOR;
        }

        object.position.set( 0.0, 0.0, 0.0 );
        object.updateMatrixWorld();

        let bBox 	= new THREE.Box3().setFromObject( object );
        const minY 	= bBox.min.y;
        const maxY 	= bBox.max.y;
        let tolerance = 0.003; // this basically means a tolerance of 0.1 inches

        if( minY >= -tolerance && maxY > 0.0 ) {
            return Constants.PlacementType.FLOOR;
        }

        else if( minY < 0.0 && maxY <= tolerance ) {
            return Constants.PlacementType.CEILING;
        }

        else if( minY < 0.0 && maxY > 0.0 ) {
            return Constants.PlacementType.WALL;
        }
    }

    /**
     * Check if material type of the asset is rug
     * @param {THREE.Scene} object - The asset object whose material type is to be checked
     */
    getMaterialType( object ) {
        if (this.itemMaterialTypes[object.name]) {
            return this.itemMaterialTypes[object.name];
        }
        else if (!this.itemMaterialTypes.hasOwnProperty(object.name)){
            if (isRug(object)) {
                return "Rug";
            }
        }
        return null;
    }

    /**
     * Sort the asset objects in terms of their sizes
     * @param {Array} array - The array of assets which need to be sorted
     * @param {Boolean} sortInDescendingOrder - The decide between the ascending/descending order while sorting
     */
    sortAssetsOnSize(array, sortInDescendingOrder) {
        // Sort all items according to size
        let itemsSortedArray = [];
        for (let item of array) {
            if (item != undefined) {
                let itemSize = new THREE.Box3().setFromObject( item ).getSize(new THREE.Vector3()).length();
                itemsSortedArray.push({
                            "object": item,
                            "size" : itemSize
                });
            }
        }

        if (sortInDescendingOrder) {
            itemsSortedArray = itemsSortedArray.sort((a,b) => b.size - a.size);
        }
        else {
            itemsSortedArray = itemsSortedArray.sort((a,b) => a.size - b.size);
        }

        let finalArray = [];
        for (let index = 0; index < itemsSortedArray.length; index++) {
            const element = itemsSortedArray[index];
            finalArray.push(element.object);
        }

        return finalArray;
    }
    
    /**
     * Setting up the asset in the scene post setting its position and rotation alongwith other properties
     * @param {THREE.Scene} asset - The asset to be added to the scene
     */
    onAssetPlacedInRoom(asset) {
        asset.updateMatrix();
        asset.updateMatrixWorld();
        this.sceneAssets.push( asset );
        this.scene.add( asset );
    }

    /**
     * Place the asset object on its fixed position
     * @param {THREE.Scene} asset - The asset to be placed
     */
    placeAssetOnFixedPosition (asset) {
        var scope = this;

        // Hard-coded stuff
        let sceneInfoStr = '{"room_id":111,"room_name":"TestRoom","cameras":[{"camera_name":"helper","camera_fov":45,"camera_position":{"x":0,"y":1.73,"z":0},"camera_target":{"x":0,"y":0,"z":-1},"camera_type":"navigation"},{"camera_name":"Bath","camera_fov":60,"camera_position":{"x":-1.8614813777262178,"y":1.4604968793475912,"z":2.9656373731391277},"camera_target":{"x":0,"y":0,"z":-1},"camera_type":"360"},{"camera_name":"Bedroom","camera_fov":60,"camera_position":{"x":-2.482347467954548,"y":1.4604952080979556,"z":-0.44658012005331327},"camera_target":{"x":0,"y":0,"z":-1},"camera_type":"360"},{"camera_name":"Closet","camera_fov":60,"camera_position":{"x":-3.7595078479022126,"y":1.460495773285735,"z":2.9525294794864116},"camera_target":{"x":0,"y":0,"z":-1},"camera_type":"360"},{"camera_name":"Kitchen","camera_fov":60,"camera_position":{"x":1.4955752878285755,"y":1.4604968787250692,"z":2.2733147453443987},"camera_target":{"x":0,"y":0,"z":-1},"camera_type":"360"},{"camera_name":"Living","camera_fov":60,"camera_position":{"x":-0.4802863372256112,"y":1.4604964083577068,"z":-1.5096977054640481},"camera_target":{"x":0,"y":0,"z":-1},"camera_type":"360"}],"sun":{"sun_position":{"x":7.907815339798513,"y":10.079306724304743,"z":9.830532374615261},"target_position":{"x":-2.17149138450623,"y":0,"z":-0.248774349689484}},"lights":[],"assets":[{"asset_id":0,"asset_type":"item","asset_name":"1385","asset_parent":-1,"asset_position":{"x":1.1461397554663595,"y":-0.000004098255052107211,"z":-1.4610233458347408},"asset_rotation":{"x":0,"y":-0.7853985000000002,"z":0},"asset_world_position":{"x":1.1461397554663595,"y":-0.000004098255052107211,"z":-1.4610233458347408},"asset_world_rotation":{"x":0,"y":-0.7853985000000003,"z":0}},{"asset_id":1,"asset_type":"item","asset_name":"1357","asset_parent":-1,"asset_position":{"x":-3.2195508661518626,"y":0.000994490949450356,"z":0.35456292591404714},"asset_rotation":{"x":0,"y":0,"z":0},"asset_world_position":{"x":-3.2195508661518626,"y":0.000994490949450356,"z":0.35456292591404714},"asset_world_rotation":{"x":0,"y":0,"z":0}},{"asset_id":2,"asset_type":"item","asset_name":"1386","asset_parent":-1,"asset_position":{"x":-1.311531422802634,"y":-0.0000054576700792654,"z":-0.10217009230690044},"asset_rotation":{"x":0,"y":-0.7853985000000003,"z":0},"asset_world_position":{"x":-1.311531422802634,"y":-0.0000054576700792654,"z":-0.10217009230690044},"asset_world_rotation":{"x":0,"y":-0.7853985000000006,"z":0}},{"asset_id":3,"asset_type":"item","asset_name":"1342","asset_parent":-1,"asset_position":{"x":0.6972261945862623,"y":-0.000004098255052107211,"z":-0.9085273546273509},"asset_rotation":{"x":0,"y":-0.7853985000000002,"z":0},"asset_world_position":{"x":0.6972261945862623,"y":-0.000004098255052107211,"z":-0.9085273546273509},"asset_world_rotation":{"x":0,"y":-0.7853985000000003,"z":0}},{"asset_id":4,"asset_type":"item","asset_name":"1358","asset_parent":-1,"asset_position":{"x":-2.777788530101216,"y":-0.000005429100948787414,"z":0.43960048944135854},"asset_rotation":{"x":0,"y":0,"z":0},"asset_world_position":{"x":-2.777788530101216,"y":-0.000005429100948787414,"z":0.43960048944135854},"asset_world_rotation":{"x":0,"y":0,"z":0}},{"asset_id":5,"asset_type":"item","asset_name":"1349","asset_parent":-1,"asset_position":{"x":2.897904345641622,"y":1.460386273287905,"z":-0.4733420392656833},"asset_rotation":{"x":0,"y":-0.7848021349484897,"z":0},"asset_world_position":{"x":2.897904345641622,"y":1.460386273287905,"z":-0.4733420392656833},"asset_world_rotation":{"x":0,"y":-0.7848021349484897,"z":0}},{"asset_id":6,"asset_type":"item","asset_name":"1353","asset_parent":-1,"asset_position":{"x":1.969853890601398,"y":1.550260138709511,"z":-1.400286829411205},"asset_rotation":{"x":0,"y":-0.7848021349484897,"z":0},"asset_world_position":{"x":1.969853890601398,"y":1.550260138709511,"z":-1.400286829411205},"asset_world_rotation":{"x":0,"y":-0.7848021349484898,"z":0}},{"asset_id":7,"asset_type":"item","asset_name":"1354","asset_parent":-1,"asset_position":{"x":0.8615299575501725,"y":1.5776063438892662,"z":-2.507290325284396},"asset_rotation":{"x":0,"y":-0.7848021349484897,"z":0},"asset_world_position":{"x":0.8615299575501725,"y":1.5776063438892662,"z":-2.507290325284396},"asset_world_rotation":{"x":0,"y":-0.7848021349484898,"z":0}},{"asset_id":8,"asset_type":"item","asset_name":"1350","asset_parent":-1,"asset_position":{"x":-2.154762516863046,"y":1.6382437507665992,"z":1.682333222870381},"asset_rotation":{"x":-3.141592653589793,"y":0,"z":-3.141592653589793},"asset_world_position":{"x":-2.154762516863046,"y":1.6382437507665992,"z":1.682333222870381},"asset_world_rotation":{"x":-3.141592653589793,"y":0,"z":-3.141592653589793}}]}';
        let sceneInfo = JSON.parse( sceneInfoStr );

        let assetFoundInHardCodedArray = false;
        for (let index = 0; index < sceneInfo.assets.length && !assetFoundInHardCodedArray; index++) {
            const element = sceneInfo.assets[index];
            if(asset == element.asset_name) {
                assetFoundInHardCodedArray = true;
                asset = element;
            }
        }

        if (assetFoundInHardCodedArray) {
            let assetObj = scope.setupObject(asset.asset_name);
            if (assetObj != undefined) {
                let assetInfo = asset;
                scope.setAssetStoredRotation(assetObj, assetInfo.asset_world_rotation); 
                scope.setAssetStoredPosition(assetObj, assetInfo.asset_world_position); 
                scope.setAssetStoredScale(assetObj, assetInfo.asset_scale); 
                scope.onAssetPlacedInRoom( assetObj );
            }
        }

        return assetFoundInHardCodedArray;
    }

    /**
     * Place loaded assets in the room
     * @param {THREE.Scene} roomObj - The room scene in which assets are to be placed
     * @param {Object} sceneRoom - The room object in which assets are to be placed
     * @param {Array} itemsToPlace - The array containing assets to place in the room
     * @param {Boolean} disableSemanticStacking - The condition to enable/disable semantic stacking
     * @param {Boolean} allowOverlapping - The condition to enable/disable overlapping while placing assets in the room
     */
    placeLoadedAssets( roomObj, sceneRoom, itemsToPlace, disableSemanticStacking = false, allowOverlapping = false) {
        if(itemsToPlace.length > 0) {
            let floorItems = [];
            let wallItems = [];
            let ceilingItems = [];
            let rugs = [];
            let stackingInfo = {};

            // Sort all items according to size
            let itemsSortedArray = this.sortAssetsOnSize(itemsToPlace, true);

            for (let item of itemsSortedArray) {
                let parentCategoriesList = [];

                let itemCategory = this.itemCategoryList[ item.name ];
                if( !disableSemanticStacking && itemCategory != undefined) {
                    let relationshipList = this.relationshipsList[ itemCategory ];
                    if(relationshipList != null && relationshipList.parents != null) {
                        parentCategoriesList = this.relationshipsList[ itemCategory ].parents;
                    }   
                }

                let semanticInfoFound = false;
                if ( !disableSemanticStacking && parentCategoriesList.length > 0 ) {
                    for ( let parentCategory of parentCategoriesList ) {
                        for ( let parentAsset of itemsSortedArray ) {
                            if ( stackingInfo[ parentAsset.id ] == undefined ) {
                                stackingInfo[ parentAsset.id ] = [];
                            }
                            
                            let parentAssetCategory = this.itemCategoryList[ parentAsset.name ];
                            if ( parentAssetCategory == parentCategory) {
                                semanticInfoFound = true;
                                stackingInfo[ parentAsset.id ].push( item );
                            }
                        }
                    }
                }

                if (!semanticInfoFound) {
                    let placementType = item.userData.placementType;

                    if(placementType == Constants.PlacementType.WALL)
                    {
                        wallItems.push(item);
                    }
                    else if (placementType == Constants.PlacementType.FLOOR)
                    {
                        if( item.userData.isRug) {
                            rugs.push(item);
                        }
                        else {
                            floorItems.push(item);
                        }
                    }
                    else if (placementType == Constants.PlacementType.CEILING)
                    {
                        ceilingItems.push(item);
                    }
                }
            }

            let unplacedItems = [];

            if (sceneRoom.floor != null) {
                if (rugs.length > 0) {
                    unplacedItems = unplacedItems.concat(this.placeRugs(roomObj, sceneRoom, rugs, allowOverlapping));
                }
            }
            else {
                unplacedItems = unplacedItems.concat(rugs);
            }

            if (sceneRoom.walls != null && sceneRoom.walls.length > 0) {
                if (wallItems.length > 0) {
                    unplacedItems = unplacedItems.concat(this.placeWallItems( roomObj, sceneRoom, wallItems, allowOverlapping));
                }
            }
            else {
                unplacedItems = unplacedItems.concat(wallItems);
            }

            if (sceneRoom.floor != null) {
                if (floorItems.length > 0) {
                    unplacedItems = unplacedItems.concat(this.placeFloorItems(roomObj, sceneRoom, floorItems, allowOverlapping));
                }
            }
            else {
                unplacedItems = unplacedItems.concat(floorItems);
            }

            if (sceneRoom.ceiling != null) {
                if (ceilingItems.length > 0) {
                    unplacedItems = unplacedItems.concat(this.placeCeilingItems(roomObj, sceneRoom, ceilingItems, allowOverlapping));
                }
            }
            else {  
                unplacedItems = unplacedItems.concat(ceilingItems);
            }

            if ( !disableSemanticStacking ) {
                this.debugEngine.debugLog("Semantic stacking info: ", stackingInfo );

                let unstackedItems = [];
                for ( let parentID in stackingInfo ) {
                    if ( stackingInfo[ parentID ].length > 0 ) {
                        let parentAsset = this.scene.getObjectById( parseInt( parentID, 10 ) );
                        
                        // Only run semantic stacking for this base object if its already placed in the scene.
                        if(this.sceneAssets.indexOf(parentAsset) != -1) {
                            let stackableItems = [];
                            for (let index = 0; index < stackingInfo[parentID].length; index++) {
                                const element = stackingInfo[parentID][index];
                                if(( !element.userData.isPlaced || element.userData.isPlaced == undefined ) ) {
                                    stackableItems.push(element);
                                }
                            }
                            
                            if(stackableItems.length > 0) {
                                this.debugEngine.debugLog("Stacking: ", stackableItems, " on ", parentAsset);
                                let unstackedItemsOfCurrentIteration = [];
                                unstackedItemsOfCurrentIteration = this.stackFloorItems( [ parentAsset ], stackableItems ); 
                                
                                // Add unstacked items of this iteration to unstackedItems array only if the items were 
                                // not already added to the unstackedItems array in previous iterations.
                                for (let index = 0; index < unstackedItemsOfCurrentIteration.length; index++) {
                                    const element = unstackedItemsOfCurrentIteration[index];
                                    if (unstackedItems.indexOf(element) == -1) {
                                        unstackedItems.push(element);
                                    }   
                                }
                            }
                        }
                    }
                }

                // Remove items that were successfully placed in sub sequent iterations from unstacked items array.
                for (let index = 0; index < unstackedItems.length; index++) {
                    const element = unstackedItems[index];
                    if (element.userData.isPlaced) {
                         removeElementFromArray(unstackedItems, element);
                    }   
                }

                // Try to place the unstacked items directly in room, without using semantic placement. The items which are still not placed are added to LFA Area.
                let remainingItems = this.placeLoadedAssets(roomObj, sceneRoom, unstackedItems, true);
                if(remainingItems != undefined) {
                    remainingItems.forEach(element => {
                        if(element != undefined) {
                            unplacedItems = unplacedItems.concat(element);
                        } 
                    });
                }
            }

            this.raycastManager.resetFarValue();

            return unplacedItems;
        }

        return itemsToPlace;

    }

    /**
     * Pass the assets to be placed to the grid objects
     * @param {Array} itemsToPlace - The array of items to be placed
     * @param {Object} grid - The grid to use for placing items in the room
     * @param {Boolean} resetForEachItem - The condition to reset grid marker 
     */
    passAssetsToGrid(itemsToPlace, grid, resetForEachItem = false, isAssetPrePlaced = false) {
        let totalItemsCount = itemsToPlace.length;
        let itemsSuccessfullyPlaced = [];

        for (let i = 0; i < totalItemsCount; i++) {
            let object = itemsToPlace[i];
            let objPlacedInRoom = false;
            let isGridFull = false;

            if(grid.canContainObj(object)) {
                while (!isGridFull && !objPlacedInRoom) {
                    objPlacedInRoom = grid.placeObjInGrid(object);
                    isGridFull = !grid.canFitObj(object);
                }

                if(objPlacedInRoom && !object.isGroup) {
                    itemsSuccessfullyPlaced.push(object);

                    let gridIntersectableObjects = grid.getIntersectableObjects();
                    if(gridIntersectableObjects != null) {
                        gridIntersectableObjects.push(object);
                    }

                    if (!isAssetPrePlaced) {
                        this.onAssetPlacedInRoom(object);
                    }
                } 
                else if (object.isGroup) {
                    let gridIntersectableObjects = grid.getIntersectableObjects();
                    if(gridIntersectableObjects != null && object.children.length) {
                        for (let asset of object.children) {
                            gridIntersectableObjects.push(asset);
                        }
                    }
                }

                if (resetForEachItem) {
                    grid.resetMarker();
                }
            }
            else {
                this.debugEngine.debugLog("Item too large for grid: ", object.name);
            }
        }

        itemsSuccessfullyPlaced.forEach(element => {
            removeElementFromArray(itemsToPlace, element);
        });

        return itemsSuccessfullyPlaced;
    }

    /**
     * Place rug items in the room using linear grid
     * @param {THREE.Scene} roomObj - The room scene in which assets are to be placed
     * @param {Object} sceneRoom - The room object in which assets are to be placed
     * @param {Array} rugs - The array of rugs to be placed in the room
     * @param {Boolean} allowOverlapping - The condition to decide to allow overlapping or not while placing rugs
     */
    placeRugsWithLinearGrid(roomObj, sceneRoom, rugs, allowOverlapping = false) {
        // Find bounding box for room
        let roomBbox 	 = new THREE.Box3().setFromObject( roomObj );
        let roomBboxSize = new THREE.Vector3();
        roomBbox.getSize(roomBboxSize);

        let offset = new THREE.Vector3(0.15, 0.15, 0.15);
        let planeOfMovement = [0,2];
        let objToUseAsFloor = sceneRoom.floor;
        let floorGrid = new LinearGrid(objToUseAsFloor, planeOfMovement, offset, this.raycastManager.raycaster, this.scene,  false, !allowOverlapping, roomBboxSize.y);

        let intersectObjects = [];
        intersectObjects.push(objToUseAsFloor);
        sceneRoom.walls.forEach(element => {
            intersectObjects.push(element);
        });
        if (sceneRoom.miscItemsParent != null ) {
            intersectObjects.push(sceneRoom.miscItemsParent);
        }

        floorGrid.setIntersectableObjects(intersectObjects);
        floorGrid.setInvertNormalsFlag(this.shouldInvertNormals);

        this.passAssetsToGrid(rugs, floorGrid, !allowOverlapping);

        this.debugEngine.debugLog("Rugs that could not be placed: ", rugs);

        return rugs;
    }

     /**
     * Place rug items in the room using matrix grid
     * @param {THREE.Scene} roomObj - The room scene in which assets are to be placed
     * @param {Object} sceneRoom - The room object in which assets are to be placed
     * @param {Array} rugs - The array of rugs to be placed in the room
     * @param {Boolean} allowOverlapping - The condition to decide to allow overlapping or not while placing rugs
     */
    placeRugsWithMatrixGrid(roomObj, sceneRoom, rugs, allowOverlapping = false) {
        let objToUseAsFloor = sceneRoom.floor;
        let floorGrid = null;
        if (this.floorRugGrids[roomObj.name]) {
            floorGrid = this.floorRugGrids[roomObj.name]
        }
        else {
            floorGrid = new MatrixGrid(sceneRoom.floor, this.raycastManager, this.scene, sceneRoom.ceiling);
            this.floorRugGrids[roomObj.name] = floorGrid;
        }

        let intersectObjects = [];
        intersectObjects.push(objToUseAsFloor);
        sceneRoom.walls.forEach(element => {
            intersectObjects.push(element);
        });
        
        if (sceneRoom.miscItemsParent != null ) {
            intersectObjects.push(sceneRoom.miscItemsParent);
        }

        floorGrid.setIntersectableObjects(intersectObjects);

        let autoPlacedItems = [];
        let unplacedItems = rugs;
        for (let i = 0; i<rugs.length; i++) {
            let element = rugs[i];
            let isPlaced = floorGrid.placeOnGrid(element)
            if (isPlaced) {
                this.onAssetPlacedInRoom(element);
                autoPlacedItems.push(element);
            }  
        }

        autoPlacedItems.forEach(element => {
            removeElementFromArray(unplacedItems, element);
        })

        this.debugEngine.debugLog("Rugs that could not be placed: ", rugs);

        return rugs;
    }

    /**
     * Place rug items in the room
     * @param {THREE.Scene} roomObj - The room scene in which assets are to be placed
     * @param {Object} sceneRoom - The room object in which assets are to be placed
     * @param {Array} rugs - The array of rugs to be placed in the room
     * @param {Boolean} allowOverlapping - The condition to decide to allow overlapping or not while placing rugs
     */
    placeRugs(roomObj, sceneRoom, rugs, allowOverlapping = false) {

        if (allowOverlapping) {
            return this.placeRugsWithLinearGrid(roomObj, sceneRoom, rugs, allowOverlapping);
        }
        else {
            return this.placeRugsWithMatrixGrid(roomObj, sceneRoom, rugs, allowOverlapping);
        }
    }

    /**
     * Place floor items in the room using linear grid
     * @param {THREE.Scene} roomObj - The room scene in which assets are to be placed
     * @param {Object} sceneRoom - The room object in which assets are to be placed
     * @param {Array} floorItems - The array of the floor items to be placed
     * @param {Boolean} allowOverlapping - The condition to decide to allow overlapping or not while placing floor assets
     */
    placeFloorItemsWithLinearGrid(roomObj, sceneRoom, floorItems, allowOverlapping = false, isAssetPrePlaced = false) {
        let prePlacedItems = [];
        this.sceneAssets.forEach(element => {
            if(element.userData.placementType == Constants.PlacementType.FLOOR && !element.userData.isRug) {
                prePlacedItems.push(element);
            }
        });

        // Find bounding box for room
        let roomBbox 	 = new THREE.Box3().setFromObject( roomObj );
        let roomBboxSize = new THREE.Vector3();
        roomBbox.getSize(roomBboxSize);

        // Find offset size for grid
        let offset = new THREE.Vector3();
        offset.x = roomBboxSize.x * 0.03;
        offset.y = roomBboxSize.y * 0.03;
        offset.z = roomBboxSize.z * 0.03;

        let planeOfMovement = [0,2];
        let objToUseAsFloor = sceneRoom.floor;
        let floorGrid = new LinearGrid(objToUseAsFloor, planeOfMovement, offset, this.raycastManager.raycaster, this.scene,  false, !allowOverlapping, roomBboxSize.y);
        let intersectObjects = [];
        if (sceneRoom.ceiling != null) {
            intersectObjects.push(sceneRoom.ceiling);
            floorGrid.setCeilingObject(sceneRoom.ceiling);
        }
        intersectObjects.push(objToUseAsFloor);
        sceneRoom.walls.forEach(element => {
            intersectObjects.push(element);
        });
        if (sceneRoom.miscItemsParent != null ) {
            if(Array.isArray(sceneRoom.miscItemsParent)) {
                sceneRoom.miscItemsParent.forEach(element => {
                    intersectObjects.push(element);
                });
            }
            else {
                intersectObjects.push(sceneRoom.miscItemsParent);
            }
        }
        prePlacedItems.forEach(element => {
            intersectObjects.push(element);
        });

        floorGrid.setIntersectableObjects(intersectObjects);
        floorGrid.setInvertNormalsFlag(this.shouldInvertNormals);
        floorGrid.setAllowedOverlapThreshold(this.allowedOverlapThreshold);

        if (this.isBabylonExported) {
            floorGrid.setInvertNormalsFlag(false);
        }

        let autoPlacedItems = [];
        autoPlacedItems = autoPlacedItems.concat(this.passAssetsToGrid(floorItems, floorGrid, !allowOverlapping, isAssetPrePlaced));

        let unplacedItems = floorItems;
        autoPlacedItems.forEach(element => {
            removeElementFromArray(unplacedItems, element);
        });

        this.debugEngine.debugLog("Floor items that could not be placed: ", unplacedItems);
        return unplacedItems;
    }

    /**
     * Place floor items in the room using matrix grid
     * @param {THREE.Scene} roomObj - The room scene in which assets are to be placed
     * @param {Object} sceneRoom - The room object in which assets are to be placed
     * @param {Array} floorItems - The array of the floor items to be placed
     * @param {Boolean} allowOverlapping - The condition to decide to allow overlapping or not while placing floor assets
     */
    placeFloorItemsWithMatrixGrid(roomObj, sceneRoom, floorItems, allowOverlapping = false) {
        let floorGrid = null;
        if (this.floorGrids[roomObj.name]) {
            floorGrid = this.floorGrids[roomObj.name];
        }
        else {
            floorGrid = new MatrixGrid(sceneRoom.floor, this.raycastManager, this.scene, sceneRoom.ceiling);
            this.floorGrids[roomObj.name] = floorGrid;
        }
        let autoPlacedItems = [];
        let intersectObjects = [];

        sceneRoom.walls.forEach(element => {
            intersectObjects.push(element);
        });

        sceneRoom.doors.forEach(element => {
            intersectObjects.push(element);
        });

        if (this.preplacedAssets != null) {
            this.preplacedAssets.forEach(element => {
                if(element.userData.visible) {
                    intersectObjects.push(element);
                }
            })
        }

        if (sceneRoom.miscItemsParent != null ) {
            if(Array.isArray(sceneRoom.miscItemsParent)) {
                sceneRoom.miscItemsParent.forEach(element => {
                    intersectObjects.push(element);
                });
            }
            else {
                intersectObjects.push(sceneRoom.miscItemsParent);
            }
        }
        
        floorGrid.setIntersectableObjects(intersectObjects);

        let unplacedItems = floorItems;
        for (let i = 0; i<floorItems.length; i++) {
            let element = floorItems[i];
            let isPlaced = floorGrid.placeOnGrid(element)
            if (isPlaced) {
                this.onAssetPlacedInRoom(element);
                autoPlacedItems.push(element);
            }  
        }

        autoPlacedItems.forEach(element => {
            removeElementFromArray(unplacedItems, element);
        })

        this.debugEngine.debugLog("Floor items that could not be placed: ", unplacedItems);
        return unplacedItems;
    }

    /**
     * Place floor items in the room
     * @param {THREE.Scene} roomObj - The room scene in which assets are to be placed
     * @param {Object} sceneRoom - The room object in which assets are to be placed
     * @param {Array} floorItems - The array of the floor items to be placed
     * @param {Boolean} allowOverlapping - The condition to decide to allow overlapping or not while placing floor assets
     */
    placeFloorItems(roomObj, sceneRoom, floorItems, allowOverlapping = false) {
        if (allowOverlapping) {
            return this.placeFloorItemsWithLinearGrid(roomObj, sceneRoom, floorItems, allowOverlapping);
        }
        else {
            return this.placeFloorItemsWithMatrixGrid(roomObj, sceneRoom, floorItems, allowOverlapping);
        }
    }

    /**
     * Place the stackable floor assets on base assets using linear grid
     * @param {Array} baseItems - The array of floor assets on which floor items can be stacked
     * @param {Array} stackableItems - The array of floor assets which can be stacked on other assets
     */
    stackFloorItemsWithLinearGrid(baseItems, stackableItems) {
        let planeOfMovement = [0,2];

        baseItems = this.sortAssetsOnSize(baseItems, true);
        stackableItems = this.sortAssetsOnSize(stackableItems, true);

        for (let index = 0; index < baseItems.length; index++) {
            let baseObj = baseItems[index];

            let baseObjSize = baseObj.userData.size;
            let offset = new THREE.Vector3();
            offset.x = baseObjSize.x * 0.10;
            offset.y = baseObjSize.y * 0.10;
            offset.z = baseObjSize.z * 0.10;

            let objGrid = new LinearGrid(baseObj, planeOfMovement, offset, this.raycastManager.raycaster, this.scene, false, true);
           
            let intersectObjects = [];
            intersectObjects.push(baseObj);
            objGrid.setIntersectableObjects(intersectObjects);

            let stackedItems = this.passAssetsToGrid(stackableItems, objGrid, true);
            for (let index = 0; index < stackedItems.length; index++) {
                const element = stackedItems[index];
                
                // Mark object as stacked for semantic placement.
                element.userData.isPlaced = true;

                element.userData.isStacked = true;

                baseObj.attach(element);
            }

            stackedItems.forEach(element => {
                removeElementFromArray(stackableItems, element);
            });
        }

        this.debugEngine.debugLog("Items that could not be stacked: ", stackableItems);
        return stackableItems;
    }

    /**
     * Place the stackable floor assets on base assets using matrix grid
     * @param {Array} baseItems - The array of floor assets on which floor items can be stacked
     * @param {Array} stackableItems - The array of floor assets which can be stacked on other assets
     */
    stackFloorItems(baseItems, stackableItems) {
        baseItems = this.sortAssetsOnSize(baseItems, true);
        stackableItems = this.sortAssetsOnSize(stackableItems, true);

        for (let index = 0; index < baseItems.length; index++) {
            let baseObj = baseItems[index];

            let objGrid = new MatrixGrid(baseObj, this.raycastManager, this.scene);
            
            let stackedItems = [];

            for(let i = 0; i < stackableItems.length; i++) {
                let isPlaced = objGrid.placeOnGrid(stackableItems[i]);
                if (isPlaced) {
                    stackedItems.push(stackableItems[i]);
                    this.onAssetPlacedInRoom(stackableItems[i]);
                    stackableItems[i].userData.isStacked = true;
                    stackableItems[i].userData.isPlaced = true;
                    baseObj.attach(stackableItems[i]);
                }
            }
            stackedItems.forEach(element => {
                removeElementFromArray(stackableItems, element);
            });
            
        }

        this.debugEngine.debugLog("Items that could not be stacked: ", stackableItems);
        return stackableItems;
    }

    /**
     * Place the ceiling assets in the room using the linear grid
     * @param {THREE.Scene} roomObj - The room scene in which assets are to be placed
     * @param {Object} sceneRoom - The room object in which assets are to be placed
     * @param {Array} ceilingItems - The ceiling assets to be placed on the room ceiling
     * @param {Boolean} allowOverlapping - The condition to decide to allow overlapping or not while placing ceiling assets
     */
    placeCeilingItemsWithLinearGrid(roomObj, sceneRoom, ceilingItems, allowOverlapping = false, isAssetPrePlaced = false) {
        let offset = new THREE.Vector3(1.0, 0.5, 1.0);
        let planeOfMovement = [0,2];
        let ceilingGrid = new LinearGrid(sceneRoom.ceiling, planeOfMovement, offset, this.raycastManager.raycaster, this.scene, true, false);

        this.passAssetsToGrid(ceilingItems, ceilingGrid, !allowOverlapping, isAssetPrePlaced);

        this.debugEngine.debugLog("Ceiling items that could not be placed: ", ceilingItems);

        return ceilingItems;
    }

    /**
     * Place the ceiling assets in the room using matrix grid
     * @param {THREE.Scene} roomObj - The room scene in which assets are to be placed
     * @param {Object} sceneRoom - The room object in which assets are to be placed
     * @param {Array} ceilingItems - The ceiling assets to be placed on the room ceiling
     * @param {Boolean} allowOverlapping - The condition to decide to allow overlapping or not while placing ceiling assets
     */
    placeCeilingItemsWithMatrixGrid(roomObj, sceneRoom, ceilingItems, allowOverlapping = false) {

        let ceilingGrid = null;
        if (this.ceilingGrids[roomObj.name]) {
            ceilingGrid = this.ceilingGrids[roomObj.name];
        }
        else {
            ceilingGrid = new MatrixGrid(sceneRoom.ceiling, this.raycastManager, this.scene, null, false);
            this.ceilingGrids[roomObj.name] = ceilingGrid;
        }

        let intersectObjects = [];
        sceneRoom.walls.forEach(element => {
            intersectObjects.push(element);
        });

        if (sceneRoom.miscItemsParent != null ) {
            intersectObjects.push(sceneRoom.miscItemsParent);
        }

        if (this.preplacedAssets != null) {
            this.preplacedAssets.forEach(element => {
                if(element.userData.visible) {
                    intersectObjects.push(element);
                }
            })
        }

        ceilingGrid.setIntersectableObjects(intersectObjects);

        let autoPlacedItems = [];
        let unplacedItems = ceilingItems;
        for (let i = 0; i<ceilingItems.length; i++) {
            let element = ceilingItems[i];
            let isPlaced = ceilingGrid.placeOnGrid(element)
            if (isPlaced) {
                this.onAssetPlacedInRoom(element);
                autoPlacedItems.push(element);
            }  
        }

        autoPlacedItems.forEach(element => {
            removeElementFromArray(unplacedItems, element);
        })

        this.debugEngine.debugLog("Ceiling items that could not be placed: ", ceilingItems);

        return ceilingItems;
    }

    /**
     * Place the ceiling assets in the room
     * @param {THREE.Scene} roomObj - The room scene in which assets are to be placed
     * @param {Object} sceneRoom - The room object in which assets are to be placed
     * @param {Array} ceilingItems - The ceiling assets to be placed on the room ceiling
     * @param {Boolean} allowOverlapping - The condition to decide to allow overlapping or not while placing ceiling assets
     */
    placeCeilingItems(roomObj, sceneRoom, ceilingItems, allowOverlapping = false) {

        if (allowOverlapping) {
            return this.placeCeilingItemsWithLinearGrid(roomObj, sceneRoom, ceilingItems, allowOverlapping);
        }
        else {
            return this.placeCeilingItemsWithMatrixGrid(roomObj, sceneRoom, ceilingItems, allowOverlapping);
        }
    }

    /**
     * 
     * @param {THREE.Scene} roomObj - The room scene in which assets are to be placed
     * @param {Object} sceneRoom - The room object in which assets are to be placed
     * @param {Array} wallItems - The wall assets to be placed on the room walls
     * @param {Boolean} allowOverlapping - The condition to decide to allow overlapping or not while placing floor assets 
     */
    placeWallItems(roomObj, sceneRoom, wallItems, allowOverlapping = false, isAssetPrePlaced = false) {
        let offset = new THREE.Vector3();
        const box  = new THREE.Box3().setFromObject( roomObj );
        const roomCenter = box.getCenter( new THREE.Vector3() );

        let raycast_point = roomCenter;
        let directionVector = new THREE.Vector3();

        // Create auto placement grids for all walls
        offset.set(0.0, 0, 0.0);
        let wallGrids = [];

        let intersectableObjects = [];
        sceneRoom.walls.forEach(element => {
            intersectableObjects.push(element);
        });
        sceneRoom.doors.forEach(element => {
            intersectableObjects.push(element);
        });
        if (sceneRoom.miscItemsParent != null ) {
            if(Array.isArray(sceneRoom.miscItemsParent)) {
                sceneRoom.miscItemsParent.forEach(element => {
                    intersectableObjects.push(element);
                });
            }
            else {
                intersectableObjects.push(sceneRoom.miscItemsParent);
            }
        }

        for (let wall of sceneRoom.walls) {
            let boxWall = new THREE.Box3().setFromObject( wall );
            let wallCenter 	= boxWall.getCenter( new THREE.Vector3() );
            let wallSize = new THREE.Vector3()
            boxWall.getSize(wallSize);

            offset.y = wallSize.y * 0.10;
            offset.x = 0.25;

            directionVector.subVectors(wallCenter, roomCenter).normalize();
            let intersect = this.raycastManager.setAndIntersect(raycast_point, directionVector, intersectableObjects);

            if (intersect && intersect.object.name == wall.name) {
                let normal = intersect.face.normal;
                if(this.shouldInvertNormals) {
                    normal = new THREE.Vector3( intersect.face.normal.x, intersect.face.normal.z, -intersect.face.normal.y );
                }

                let isInside = false;
                for (let index = 0; index < 3; index++) {
                    const element = normal.getComponent(index);
                    if(element != 0) {
                        isInside = (element < 0);
                    }
                }

                this.debugEngine.debugLog("Wall : ", wall.name, ", normal : ", normal );
                this.debugEngine.debugLog(isInside);  

                let wallGrid = new WallGrid(wall, new THREE.Vector3().copy(offset), this.raycastManager.raycaster, this.scene, isInside, !allowOverlapping, true, normal);
                wallGrid.setIntersectableObjects(intersectableObjects);
                wallGrid.setInvertNormalsFlag(this.shouldInvertNormals);
                wallGrid.setAllowedOverlapThreshold(this.allowedOverlapThreshold);
                wallGrids.push(wallGrid);
                
            }
        }

        // Place wall items on the walls
        wallItems = this.sortAssetsOnSize(wallItems, false);
        wallGrids.forEach(wallGrid => {
            this.passAssetsToGrid(wallItems, wallGrid, false, isAssetPrePlaced);
        });

        this.debugEngine.debugLog("Wall items that could not be placed: ", wallItems);

        return wallItems;
    }

    /**
     * The manager function to auto place assets in the space
     * @param {Object} collections - A dictionary collection of assets in the scene
     * @param {Function} onLoadAction - The callback function to be called once the auto placement of assets is done
     */
    autoPlaceAssets(collections, onLoadAction, preplacedAssets = null) {
        this.debugEngine.debugLog("Auto placement");
        let action = onLoadAction;
        this.preplacedAssets = preplacedAssets;
        this.debugEngine.setIntervalStartToCurrentTime();
        // Step 1: Try placing each item of collection in their respective space units
        for (let key in this.space.areas) {
            const area = this.space.areas[key];

            let loadedItems = [];
            for(let index in collections[key]) {
                let assetFoundInHardCodedArray = false;
                if(this.runHardCodedPlacement) {
                    assetFoundInHardCodedArray = this.placeAssetOnFixedPosition(collections[key][index]);
                } 

                if(!assetFoundInHardCodedArray) {
                    let assetObj = this.setupObject(collections[key][index]);
                    if(assetObj != undefined) {
                        loadedItems.push( assetObj );
                    }
                }   
            }

            let unplacedItems = this.placeLoadedAssets(area.root, area, loadedItems );
            if (unplacedItems != undefined) {
                unplacedItems.forEach(element => {
                    this.lostAndFoundAreaItems.push(element);
                });
            }
        }

        // Step 2: Try placing remaining items in any space unit they can fit in
        // Copy remaining items in new array
        let itemsLeftToPlace = [...this.lostAndFoundAreaItems];
        // Clear LFAArea items array
        this.lostAndFoundAreaItems = [];

        this.debugEngine.debugLog("Step 2, Remaining items: ", itemsLeftToPlace);
        let unplacedItems = itemsLeftToPlace;
        for (let key in this.space.areas) {
            const area = this.space.areas[key];
            unplacedItems = this.placeLoadedAssets(area.root, area, unplacedItems );
        }

        // Step 3: Try placing remaining items with overlapping threshold enabled.
        this.debugEngine.debugLog("Step 3, Remaining items: ", unplacedItems);
        this.allowedOverlapThreshold = 0.15;
        for (let key in this.space.areas) {
            const area = this.space.areas[key];
            unplacedItems = this.placeLoadedAssets(area.root, area, unplacedItems, false, true );
        }
        // Step 4: Try placing remaining items any where on the complete floor without any overlapping checks.
        this.allowedOverlapThreshold = 0;
        let spaceObj = {
            root: null,
            ceiling: null,
            floor: null,
            walls: [],
            miscItemsParent: null,
            doors: []
        };
        spaceObj.root = this.space.scene;
        spaceObj.walls = this.space.walls;
        spaceObj.ceiling = this.space.ceilings;
        spaceObj.floor = this.space.floors;
        spaceObj.miscItemsParent = this.space.miscNodes;
        spaceObj.doors = this.space.doors;
        unplacedItems = this.placeLoadedAssets(spaceObj.root, spaceObj, unplacedItems, false, true);

        unplacedItems.forEach(element => {
            this.lostAndFoundAreaItems.push(element);
        });
        this.debugEngine.logIntervalTime("auto-placement done");
        this.setMiscAssetsInScene();
        this.setupLostAndFoundArea();
        this.raycastManager.resetFarValue();
        this.raycastManager.setAssetsList( this.sceneAssets );
        this.raycastManager.buildFocusTargetList();
        action();
    }

    /**
     * Set stored rotation of asset in json
     * @param {THREE.Scene} assetObj - The Object Scene for asset whose stored rotation is being set
     * @param {Object} assetRotation - The rotation of asset stored in scene save data
     */
    setAssetStoredRotation(assetObj, assetRotation) {
        if ( assetRotation.type == undefined ) {
            let assetEuler = new THREE.Euler( assetRotation.x, assetRotation.y, assetRotation.z );
            assetObj.setRotationFromEuler( assetEuler );
        }
        else {
            assetObj.quaternion.set( assetRotation.x, assetRotation.y, assetRotation.z, assetRotation.w);
        }
    }

    /**
     * Set stored position of asset in json
     * @param {THREE.Scene} assetObj - The Object Scene for asset whose stored position is being set
     * @param {Object} assetPosition - The position of asset stored in scene save data
     */
    setAssetStoredPosition(assetObj, assetPosition) {
        assetObj.position.set( assetPosition.x, assetPosition.y, assetPosition.z );
    }

    /**
     * Set stored scale of asset in json
     * @param {THREE.Scene} assetObj - The Object Scene for asset whose stored scale is being set
     * @param {Object} assetScale - The scale of asset stored in scene save data
     */
    setAssetStoredScale(asset, assetScale) {
        let assetObj = getObjectFromRootByName(asset, asset.name);
        if (assetObj && assetScale) {
            assetObj.scale.set( assetScale.x, assetScale.y, assetScale.z );
            asset.userData.scale.set(assetScale.x, assetScale.y, assetScale.z);
            asset.userData.pvb.update();
        }
    }

    /**
     * Remove stored asset transform
     * @param {THREE.Scene} assetObj - The Object Scene for asset whose restore transform is being removed
     */
    removeAssetRestoreTransform(assetObj) {
        assetObj.userData.lastValidPosition = null;
        assetObj.userData.lastValidQuaternion = null;
        assetObj.userData.lastValidParent = null;
    }

    /**
     * Set asset restore transfrom info
     * @param {THREE.Scene} assetObj - The Object Scene for asset whose transform is being stored
     */
    setAssetRestoreTransform(assetObj) {
        const worldPosition = new THREE.Vector3();
        const worldQuaternion = new THREE.Quaternion();
        const worldScale = new THREE.Vector3();
        assetObj.matrixWorld.decompose ( worldPosition, worldQuaternion, worldScale );
        assetObj.userData.lastValidPosition = worldPosition;
        assetObj.userData.lastValidQuaternion = worldQuaternion;
        assetObj.userData.lastValidParent = assetObj.parent;
    }

    /**
     * Place saved/recorded assets on positions retrieved from the scene load info as they get loaded one by one
     * @param {Number} index - The asset index in the stored assets list of the loaded asset
     * @param {Boolean} attachChildrenToParent - The condition to decide to set all parent child relationships once all assets are loaded in the scene
     */
    placeStoredAsset(index) {
        var scope = this;
        let asset = scope.sceneToLoadAssets[index];
        let assetObj = scope.setupObject(asset.asset_name, asset.asset_visible,asset.currentPlacement);
        assetObj.userData.currentPlacement = asset.currentPlacement;
        assetObj.userData.originalPlacement = asset.originalPlacement;
        scope.orderedAssetList[asset.asset_id] = assetObj;
        scope.onAssetPlacedInRoom(assetObj);
        scope.setAssetStoredRotation(assetObj, asset.asset_world_rotation);
        scope.setAssetStoredPosition(assetObj, asset.asset_world_position);
        scope.setAssetStoredScale(assetObj, asset.asset_scale);
        if ( asset.asset_parent != -1 && scope.orderedAssetList[asset.asset_parent] != undefined ) {
            scope.orderedAssetList[asset.asset_parent].attach(assetObj);
            assetObj.userData.isStacked = true;
        }
        else if (asset.asset_parent != -1 && scope.orderedAssetList[asset.asset_parent] == undefined ) {
            scope.orphans[asset.asset_id] = asset;
        }

    }

    /**
     * Connect child assets to their parents when all the stored assets are placed in the scene.
     */
    onStoredAssetsPlaced() {
        // handle parent child relationships
        var scope = this;
        for (const [orphanIndex, orphanValue] of Object.entries(scope.orphans)) {
            let orphanObj = scope.orderedAssetList[orphanIndex]
            if (scope.orderedAssetList[orphanValue.asset_parent]) {
                scope.orderedAssetList[orphanValue.asset_parent].attach(orphanObj);
                orphanObj.userData.isStacked = true;
                orphanObj.updateMatrix();
                orphanObj.updateMatrixWorld();  
            }
            else {
                scope.setAssetStoredRotation(orphanObj, orphanValue.asset_world_rotation);
                scope.setAssetStoredPosition(orphanObj, orphanValue.asset_world_position);
                scope.setAssetStoredScale(orphanObj, orphanValue.asset_scale);
            }
        }
    }

    /**
     * Clone an asset along with custom user data properties e.g PVB, ConvexHull etc.
     * @param {THREE.Scene} asset - The asset to clone.
     */
    cloneAsset( asset ) {
        //Attach object to world to set world transformations
        const parent = asset.parent; 
        this.scene.attach(asset);
        // Remove PVB object form asset before cloning.
        let pvbOfOriginalAsset = asset.userData.pvb;
        let convexHullOfOriginalAsset = asset.userData.convexGeometry;

        asset.remove(pvbOfOriginalAsset);
        if(convexHullOfOriginalAsset) {
            asset.remove(convexHullOfOriginalAsset);
        }

        let detachedChildren = detachSceneAssets(this.scene, asset);
        this.removeAssetRestoreTransform(asset);
        let clonedAsset = asset.clone();
        this.setAssetRestoreTransform(asset);
        attachSceneAssets(asset, detachedChildren);

        // Re attach PVB & convex hull after cloning.
        if(convexHullOfOriginalAsset) {
            asset.add(convexHullOfOriginalAsset);
            asset.userData.convexGeometry = convexHullOfOriginalAsset;

            // Clone convexhull object
            let clonedConvexHull = convexHullOfOriginalAsset.clone();
            clonedAsset.add(clonedConvexHull);
            clonedAsset.userData.convexGeometry = clonedConvexHull;
        }
        asset.add(pvbOfOriginalAsset);
        asset.userData.pvb = pvbOfOriginalAsset;

        clonedAsset.traverse ( ( child ) => {
            if ( child.isMesh ) {
                child.material = child.material.clone();
                child.userData.rootNode = clonedAsset.id;
            }
        } );

		clonedAsset.userData.placementType 	= asset.userData.placementType;
        clonedAsset.userData.assetType 		= asset.userData.assetType;
        clonedAsset.userData.size           = asset.userData.size;
        clonedAsset.userData.scale          = asset.userData.scale;
        clonedAsset.userData.pvb            = new PVB(clonedAsset);
        
        if(asset.userData.isPillow) {
            clonedAsset.userData.isPillow = true;
        }

        this.onAssetPlacedInRoom( clonedAsset );
        //Re-attach object to original parent
        parent.attach(asset);
        parent.attach(clonedAsset);

        return clonedAsset;
    }

    /**
     * Clone currently selected asset
     * @param {THREE.Scene} asset - The scene asset to be cloned
     * @param {String} placementType - The placement type floor/wall/ceiling of the asset to be cloned
     */
    cloneSelectedAsset( asset, placementType) {
            let clonedAsset = this.cloneAsset( asset );

            if (placementType == Constants.PlacementType.FLOOR || placementType == Constants.PlacementType.CEILING ) {

                clonedAsset.translateOnAxis( Constants._unit.X, clonedAsset.userData.size.x / 4.0 );
                clonedAsset.translateOnAxis( Constants._unit.Z, clonedAsset.userData.size.z / 4.0 );
                
            }
            else if ( placementType == Constants.PlacementType.WALL ) {
                clonedAsset.translateOnAxis( Constants._unit.X, clonedAsset.userData.size.x );
            }

            this.raycastManager.setAssetsList( this.sceneAssets );
            this.raycastManager.buildFocusTargetList();
            return clonedAsset;
    }

    /**
     * Swap the asset in scene by setting it up, copying orignal asset position and rotation
     * @param {String} asset - The target asset - currently in the scene
     * @param {String} swappedAssetId - The asset who will replace the target asset
     */
    swapSelectedAsset( asset, swappedAssetId) {
        //Attach object to world to set world transformations
        const parent = asset.parent;
        this.scene.attach(asset);
        let detachedChildren = detachSceneAssets(this.scene, asset);
        let swappedAsset = this.setupObject(swappedAssetId);

        if (swappedAsset == undefined) {
            return;
        }

        swappedAsset.position.copy(asset.position);
        swappedAsset.rotation.copy(asset.rotation);
        attachSceneAssets(swappedAsset, detachedChildren);
        swappedAsset.scale.copy(asset.scale);
        
        if (swappedAsset.children.length > 0) {
            swappedAsset.children[0].scale.copy(asset.children[0].scale);
        }

        this.onAssetPlacedInRoom( swappedAsset );
        //Re-attach object to original parent
        parent.attach(asset);
        parent.attach(swappedAsset);    

        this.raycastManager.setAssetsList( this.sceneAssets );
        this.raycastManager.buildFocusTargetList();
        return swappedAsset;
    }

    setBabylonExported (value) {
        this.isBabylonExported = value;
    }

    /**
     * Auto-place group of assets using linear floor grid
     * @param {String} groupedAssets - List of groups 
     *  @param {String} isBabylonExported - Boolean indicating if space is babylon exported
     */
    autoPlaceGroup (groupedAssets, isBabylonExported) {
        this.isBabylonExported = isBabylonExported;
        for (let key in this.space.areas) {
            const area = this.space.areas[key];
            this.placeFloorItemsWithLinearGrid(area.root, area, groupedAssets, false, isBabylonExported);
            break;
        }
        this.raycastManager.resetFarValue();
        this.raycastManager.setAssetsList( this.sceneAssets );
        this.raycastManager.buildFocusTargetList();
    }

    /**
     * Auto-place the wall items using grid
     * @param {String} wallAssets - List of wall type assets
     */
    autoPlaceWallAssets (wallAssets) {
        for (let key in this.space.areas) {
            const area = this.space.areas[key];
            if (area.walls != null && area.walls.length > 0) {
                this.placeWallItems(area.root, area, wallAssets, true, true);
                break;
            }
        }
        this.raycastManager.resetFarValue();
        this.raycastManager.setAssetsList( this.sceneAssets );
        this.raycastManager.buildFocusTargetList();
    }

     /**
     * Auto-place the floor items using grid
     * @param {String} wallAssets - List of wall type assets
     */
     autoPlaceFloorAssets (floorAssets) {
        for (let key in this.space.areas) {
            const area = this.space.areas[key];
            if (area.floor){
                this.placeFloorItemsWithLinearGrid(area.root, area, floorAssets, false, true);
                break;
            }
        }
        this.raycastManager.resetFarValue();
        this.raycastManager.buildFocusTargetList();
    }

    /**
    * Auto-place the ceiling items using grid
    * @param {String} wallAssets - List of wall type assets
    */
    autoPlaceCeilingAssets (ceilingAssets) {
        for (let key in this.space.areas) {
            const area = this.space.areas[key];
            if (area.ceiling) {
                this.placeCeilingItemsWithLinearGrid(area.root, area, ceilingAssets, false, true);
                break;
            }
        }
        this.raycastManager.resetFarValue();
        this.raycastManager.buildFocusTargetList();
    }

    /**
    * Auto-place list of items
    * @param {String} assets - List of wall type assets
    */
    autoPlaceLostAssets (assets) {
        const wallItems = assets.filter(
          (item) => item.userData.placementType == Constants.PlacementType.WALL
        );

        const floorItems = assets.filter(
          (item) => item.userData.placementType == Constants.PlacementType.FLOOR
        );

        const ceilingItems = assets.filter(
          (item) =>
            item.userData.placementType == Constants.PlacementType.CEILING
        );

        this.autoPlaceWallAssets(wallItems);
        this.autoPlaceFloorAssets(floorItems);
        this.autoPlaceCeilingAssets(ceilingItems);
    }

    /**
     * Place the assets from deleted assets array
     * @param {String} assets - The target assets - from history
     *  @param {String} action - Callback function to be called after placing assets
     */
    placeDeletedAssets( assets, action) {
        for (let asset of assets)
        {
            let addedAsset = this.setupObject(asset.asset_name);

            if (addedAsset == undefined) {
                return;
            }

            addedAsset.position.copy(asset.asset_position);
            addedAsset.rotation.copy(asset.asset_rotation);
            addedAsset.scale.copy(asset.asset_scale);

            this.onAssetPlacedInRoom( addedAsset );
            this.raycastManager.setAssetsList( this.sceneAssets );
            this.raycastManager.buildFocusTargetList();
        }

        action();
    }
}
